import React, { useCallback, useEffect, useRef } from "react";
import Cursor from "../av/Cursor";
import { useAtom } from "jotai";
import {
  currentUserAtom,
  earHeightAtom,
  streamsAtom,
  useScrollRef,
  useMouseRef,
} from "../jotai/atoms";
import { useMotionValue } from "framer-motion";
import { AnyBlock, Point2D, Size } from "../types";
import { setAudioListenerLocation } from "./PositionalAudio";
import { usePositions } from "../roomservice/presences";
import { useOnlines } from "../jotai/atoms";
import { addPosition } from "../utils/gridPosition";
import { useUserInfos } from "../roomservice/maps";
import { BOT_NAME, ROOM_SERVICE_MAP_KEYS } from "../consts";
import useThrottle from "../utils/useThrottle";

function CursorList({
  windowSize,
  upsertBlock,
}: {
  windowSize: Size;
  upsertBlock: (block: AnyBlock) => void;
}) {
  const mousePositionRef = useMouseRef();
  const [currentUser] = useAtom(currentUserAtom);
  const onlines = useOnlines();
  const userInfos = useUserInfos();
  const [streams] = useAtom(streamsAtom);
  const [earHeight] = useAtom(earHeightAtom);
  const currentUserX = useMotionValue(0);
  const currentUserY = useMotionValue(0);
  const clientX = useRef(0);
  const clientY = useRef(0);
  const [, positionClient] = usePositions();
  const scroll = useScrollRef();

  const updateCursor = useCallback(
    (pos: Point2D) => {
      positionClient!.set(
        pos,
        24 * 60 * 60 // 1 day?
      );
      currentUserX.set(pos.x);
      currentUserY.set(pos.y);
      mousePositionRef!.current.x = pos.x;
      mousePositionRef!.current.y = pos.y;
    },
    [currentUserX, currentUserY, mousePositionRef, positionClient]
  );

  function onMouseMove(e: any) {
    updateCursor({ x: e.pageX, y: e.pageY });

    // store these values so we know where the mouse is, useful for scroll.
    clientX.current = e.clientX;
    clientY.current = e.clientY;
  }
  // it's important to throttle this, because sometimes mousemove and dragover both fire
  const onMouseMoveThrottled = useThrottle(onMouseMove, 1000 / 60);

  // Manage current user mouse position in canvas space
  useEffect(() => {
    if (!positionClient || !mousePositionRef) return;

    function onScroll() {
      if (!scroll?.current) return;
      updateCursor(
        addPosition({ x: clientX.current, y: clientY.current }, scroll.current)
      );

      setAudioListenerLocation(
        document.documentElement.scrollLeft + window.innerWidth / 2,
        document.documentElement.scrollTop + window.innerHeight / 2,
        earHeight
      );
    }

    function onResize() {
      setAudioListenerLocation(
        document.documentElement.scrollLeft + window.innerWidth / 2,
        document.documentElement.scrollTop + window.innerHeight / 2,
        earHeight
      );
    }

    // use window instead of canvasRef here so cursor is always
    // present and doesn't disappear when hovering over chrome
    // or any children
    window.addEventListener("mousemove", onMouseMoveThrottled);
    window.addEventListener("dragover", onMouseMoveThrottled);
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("mousemove", onMouseMoveThrottled);
      window.removeEventListener("dragover", onMouseMoveThrottled);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [
    positionClient,
    currentUserX,
    currentUserY,
    mousePositionRef,
    earHeight,
    scroll,
    windowSize,
    onMouseMoveThrottled,
    updateCursor,
  ]);

  return onlines ? (
    <ul aria-hidden={true}>
      {onlines.map((uid: string) => {
        let userInfo = userInfos[ROOM_SERVICE_MAP_KEYS.USER + uid];
        if (!userInfo) return null;
        if (userInfo.name === BOT_NAME) return null;
        return (
          <li key={uid}>
            <Cursor
              userId={uid}
              isSelf={currentUser!.id === uid}
              stream={streams[uid]}
              currentUserPos={{ x: currentUserX, y: currentUserY }}
              isStreaming={userInfo?.video}
              isMuted={!userInfo?.audio}
              upsertBlock={upsertBlock}
            ></Cursor>
          </li>
        );
      })}
    </ul>
  ) : (
    <span>No participants yet</span>
  );
}

export default CursorList;
